import Menu from '@/global/components/MenuV2'
import { Badge } from '@/global/components/ui/badge'
import { cn } from '@/lib/utils'
import localeText from '@/locales/datagrid'
import { Button } from '@/global/components/Button'
import {
    Columns3Icon,
    ListFilterIcon,
    ListIcon,
    UploadIcon,
} from 'lucide-react'
import { useTableApiContext } from '../../utils/table-context'
import ToolbarDensitySelector from './DensitySelector'
import ExportarCsv from './ExportarCsv'
import { GridButton, GridMenuButton } from './MenuComponents'

interface Props {
    children?: React.ReactNode
    buttonsProps: IButtonsProp
}

export default function GridToolbarLeftSide({ children, buttonsProps }: Props) {
    return [
        <div
            key="desktop"
            className="hidden md:flex items-center flex-1 flex-wrap"
        >
            <ButtonsSlot {...buttonsProps} />
            {children}
        </div>,
        <div
            key="mobile"
            className="flex md:hidden items-center flex-1 flex-wrap"
        >
            <Menu.Root>
                <Menu.Trigger>
                    <Button
                        variant="outlined"
                        title="Mostrar menu"
                        className="text-nowrap gap-2"
                        startIcon={<ListIcon />}
                        data-testid="botao_opcoes_datatable"
                    >
                        Opções
                    </Button>
                </Menu.Trigger>
                <Menu.Popover>
                    <div className="flex flex-col gap-1 justify-start">
                        <ButtonsSlot {...buttonsProps} />
                        {children}
                    </div>
                </Menu.Popover>
            </Menu.Root>
        </div>,
    ]
}

interface IButtonsProp {
    hideFilterButton: boolean
    hideDensitySelector: boolean
    hideExportButton: boolean
    showVisibilityButton: boolean
    exportButtonProps: {
        children?: React.ReactNode
        fileName?: string
    }
}

function ButtonsSlot({
    exportButtonProps,
    hideDensitySelector,
    hideExportButton,
    hideFilterButton,
    showVisibilityButton,
}: IButtonsProp) {
    const {
        filterLength,
        setVisibilidadeColunas,
        setFiltroVisivel,
        visibilidadeColunas,
        isFiltroVisivel,
    } = useTableApiContext()

    return [
        !hideFilterButton && (
            <div className="relative" key="filter-button">
                <Badge
                    className="absolute top-0.5 right-0.5 size-5"
                    key={`filter-${isFiltroVisivel}`}
                >
                    {filterLength}
                </Badge>
                <GridButton
                    startIcon={<ListFilterIcon />}
                    label={`${localeText.toolbarFilters}`}
                    onClick={() => {
                        setVisibilidadeColunas(false)
                        setFiltroVisivel(_ => !_)
                    }}
                    className={cn('transition-colors duration-200', {
                        'bg-primary/15': isFiltroVisivel,
                    })}
                />
            </div>
        ),
        !!showVisibilityButton && (
            <GridButton
                key="visibility"
                startIcon={<Columns3Icon />}
                label={`${localeText.toolbarColumns}`}
                onClick={() => {
                    setFiltroVisivel(false)
                    setVisibilidadeColunas(_ => !_)
                }}
                className={cn('transition-colors duration-200', {
                    'bg-primary/15': visibilidadeColunas,
                })}
            />
        ),
        !hideDensitySelector && <ToolbarDensitySelector key="density" />,
        !hideExportButton && !exportButtonProps?.children && (
            <ExportarCsv
                key="export-csv"
                fileName={exportButtonProps?.fileName}
            />
        ),
        !!exportButtonProps?.children && (
            <GridMenuButton
                key="export"
                startIcon={<UploadIcon />}
                label={`${localeText.toolbarExport}`}
            >
                {exportButtonProps.children}
            </GridMenuButton>
        ),
    ]
}
